/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

var version="3.3.0";

/** @internal */
function getVisit() {
    var _a;
    // @ts-ignore
    let visit = (_a = window === null || window === void 0 ? void 0 : window.tracking) === null || _a === void 0 ? void 0 : _a.visit();
    if (visit) {
        return visit;
    }
    const cookie = document.cookie
        .split(";")
        .find((c) => c.trim().startsWith("visit"));
    if (cookie) {
        visit = cookie.split("=")[1].trim();
    }
    return visit;
}
/** @internal */
function initLogging() {
    const sendRequest = (payload) => __awaiter(this, void 0, void 0, function* () {
        return yield fetch(getLoggingUrl(window.location.hostname), {
            method: "POST",
            body: payload,
            headers: {
                "Content-Type": "application/json",
            },
        });
    });
    return {
        log: (message) => __awaiter(this, void 0, void 0, function* () {
            try {
                let messageObject = {};
                if (typeof message === "string") {
                    messageObject.message = message;
                }
                else {
                    messageObject = Object.assign({}, message);
                }
                yield sendRequest(JSON.stringify([
                    {
                        common: {
                            attributes: {
                                host: window.location.href,
                                agent: window.navigator.userAgent,
                                serviceName: "ab-reader",
                                visit: getVisit(),
                                version,
                            },
                        },
                        logs: [Object.assign(Object.assign({}, messageObject), { logtype: "browser" })],
                    },
                ]));
            }
            catch (e) {
                console.error("ab-reader failed sending logs", e === null || e === void 0 ? void 0 : e.message);
            }
        }),
    };
}
function getLoggingUrl(hostname) {
    let scriptSrc = "https://nrlogger-prod.cf-exp.vpsvc.com?partition=exp";
    if (hostname === "localhost" || hostname.startsWith("staging")) {
        scriptSrc = "https://nrlogger-dev.cf-exp.vpsvc.com?partition=exp";
    }
    return scriptSrc;
}

/** @internal */
const isWindowUndefined = () => typeof window === "undefined";

const getABReader = () => (window === null || window === void 0 ? void 0 : window.abReader) || {
    getVariation: () => null,
    getAllExperiments: () => [],
    checkFeature: () => null,
    isAvailable: () => false,
    getTestUserId: () => null,
    fireImpression: () => false,
    fireFeatureImpression: () => false,
};
let logWarnings = {};
const logger = initLogging();
const scriptLoadedEventName = "ab-script-loaded";
const initialize = () => {
    logWarnings = {};
    if (isWindowUndefined()) {
        return;
    }
    //inject script from cdn
    if (window.abReaderInitialized)
        return;
    const script = document.createElement("script");
    script.type = "application/javascript";
    script.async = true;
    script.src = getScriptSourceUrl(window.location.hostname);
    script.onload = () => window.dispatchEvent(new CustomEvent(scriptLoadedEventName));
    // Insert our script next to the first script element.
    document.head.appendChild(script);
    window.abReaderInitialized = true;
};
const failSafeAndLog = (func, functionName, defaultValue) => {
    try {
        return func();
    }
    catch (error) {
        logInternal({
            message: `ab-reader library failed when calling ${functionName}.`,
            error: JSON.stringify(error),
        });
    }
    return defaultValue;
};
const isAvailable = () => {
    if (!checkForInitialization("isAvailable")) {
        return false;
    }
    return isAvailableInternal();
};
const isAvailableInternal = () => {
    return failSafeAndLog(() => { var _a, _b; return (_b = (_a = window.abReader) === null || _a === void 0 ? void 0 : _a.isAvailable()) !== null && _b !== void 0 ? _b : false; }, "isAvailable", false);
};
const checkFeature = (featureName) => {
    if (!checkForInitializationAndAvailability("checkFeature", featureName)) {
        return null;
    }
    return failSafeAndLog(() => getABReader().checkFeature(featureName), "checkFeature", null);
};
const getVariation = (experimentKey) => {
    if (!checkForInitializationAndAvailability("getVariation", experimentKey)) {
        return null;
    }
    return failSafeAndLog(() => getABReader().getVariation(experimentKey), "getVariation", null);
};
const getAllExperiments = () => {
    if (!checkForInitializationAndAvailability("getAllExperiments")) {
        return [];
    }
    return failSafeAndLog(getABReader().getAllExperiments, "getAllExperiments", []);
};
const fireImpression = (experimentKey, variationKey, pageParameters) => {
    if (isWindowUndefined()) {
        logInternal({
            message: `fireImpression called serverside. `,
            error: "Tracking is not available.",
        });
    }
    const fireImpressionInternal = () => failSafeAndLog(() => getABReader().fireImpression(experimentKey, variationKey, pageParameters || null), "fireImpression", false);
    const fireImpressionCallback = () => __awaiter(void 0, void 0, void 0, function* () {
        fireImpressionInternal();
        window.removeEventListener(scriptLoadedEventName, fireImpressionCallback);
    });
    if (!checkForInitializationAndAvailability("fireImpression")) {
        if (!window.abReaderInitialized) {
            initialize();
        }
        window.addEventListener(scriptLoadedEventName, fireImpressionCallback);
        return false;
    }
    return fireImpressionInternal();
};
const fireFeatureImpression = (feature) => {
    if (isWindowUndefined()) {
        logInternal({
            message: `fireFeatureImpression called serverside. `,
            error: "Tracking is not available.",
        });
    }
    const fireFeatureImpressionInternal = () => failSafeAndLog(() => getABReader().fireFeatureImpression(feature.featureName, feature.ruleName, feature.enabled), "fireFeatureImpression", false);
    const fireFeatureImpressionCallback = () => __awaiter(void 0, void 0, void 0, function* () {
        fireFeatureImpressionInternal();
        window.removeEventListener(scriptLoadedEventName, fireFeatureImpressionCallback);
    });
    if (!checkForInitializationAndAvailability("fireFeatureImpression")) {
        if (!window.abReaderInitialized) {
            initialize();
        }
        window.addEventListener(scriptLoadedEventName, fireFeatureImpressionCallback);
        return false;
    }
    return fireFeatureImpressionInternal();
};
const getTestUserId = () => {
    if (!checkForInitializationAndAvailability("getTestUserId")) {
        return null;
    }
    return failSafeAndLog(getABReader().getTestUserId, "getTestUserId", null);
};
const waitTillAvailable = (timeout) => new Promise((resolve) => {
    whenAvailable((available) => resolve(!!available), timeout);
});
const whenAvailable = (callback, timeout) => {
    if (isWindowUndefined()) {
        callback(false);
        return;
    }
    if (isAvailableInternal()) {
        callback(true);
        return;
    }
    let isAvailableResolve;
    const available = new Promise((resolve) => {
        isAvailableResolve = resolve;
    });
    const scriptHandler = () => {
        window.removeEventListener(scriptLoadedEventName, scriptHandler);
        if (isAvailableInternal()) {
            isAvailableResolve(true);
        }
        else {
            initVariationsHandler();
        }
    };
    const initScriptHandler = () => {
        window.addEventListener(scriptLoadedEventName, scriptHandler);
    };
    const variationsResolvedEventName = "variationsResolved";
    const variationsHandler = () => {
        window.removeEventListener(variationsResolvedEventName, variationsHandler);
        isAvailableResolve(true);
    };
    const initVariationsHandler = () => {
        window.addEventListener(variationsResolvedEventName, variationsHandler);
    };
    const promises = [available];
    if (timeout) {
        promises.push(new Promise((resolve) => {
            setTimeout(resolve, timeout);
        }));
    }
    if (!window.abReader) {
        initScriptHandler();
    }
    else {
        initVariationsHandler();
    }
    Promise.race(promises).then(() => {
        callback(isAvailableInternal());
    });
};
const activate = (experimentKey) => {
    const variation = getVariation(experimentKey);
    if (variation) {
        fireImpression(experimentKey, variation);
    }
    return variation;
};
const isFeatureEnabled = (featureName) => {
    const feature = checkFeature(featureName);
    if (feature !== null) {
        fireFeatureImpression(feature);
        if (feature.enabled === "true") {
            return true;
        }
    }
    return false;
};
const checkForInitialization = (methodName, experimentKey) => {
    if (isWindowUndefined()) {
        return false;
    }
    const experimentKeyMessage = experimentKey
        ? ` with experiment key [${experimentKey}]`
        : "";
    if (!window.abReaderInitialized) {
        logInternal({
            message: `Library not initialized before using method ${methodName}${experimentKeyMessage}.`,
        });
        return false;
    }
    return true;
};
const checkForInitializationAndAvailability = (methodName, experimentKey) => {
    if (!checkForInitialization(methodName, experimentKey)) {
        return false;
    }
    const experimentKeyMessage = experimentKey
        ? ` with experiment key [${experimentKey}]`
        : "";
    if (window.abReader === undefined) {
        logInternal({
            message: `ab-reader library not ready before using method ${methodName}${experimentKeyMessage}.`,
        });
        return false;
    }
    return true;
};
function getScriptSourceUrl(hostname) {
    let scriptSrc = `https://wexp.exp.vpsvc.com/experimentation/ab-lib/ab-reader.js`;
    try {
        let result = hostname.match(/vistaprint\.[a-z]{2,3}(\.[a-z]{2,3})?/);
        if (result && result[0]) {
            scriptSrc = `https://www.${result[0]}/experimentation/ab-lib/ab-reader.js`;
        }
        if (hostname === "localhost" || hostname.startsWith("staging.vistaprint")) {
            scriptSrc =
                "https://staging.vistaprint.com/experimentation/ab-lib/ab-reader.js";
        }
        return scriptSrc;
    }
    catch (error) {
        logInternal({
            message: `There was an error trying to get the script source for the following hostname: '${hostname}'`,
        });
        return scriptSrc;
    }
}
function logInternal(message) {
    // @ts-ignore:next-line
    const messageKey = typeof message === "string" ? message : message.message;
    if (!logWarnings[messageKey]) {
        logger.log(message);
        logWarnings[messageKey] = 1;
    }
}

export { activate, fireImpression, getAllExperiments, getTestUserId, getVariation, initialize, isAvailable, isFeatureEnabled, waitTillAvailable, whenAvailable };
//# sourceMappingURL=main.js.map
